import axios from "axios";
import { logger } from "../utils/logger";
import { getImageUrlsFromMedia, isImageUrl } from "../utils/imageUtils";
import { createCloudRunAxios } from "./axios";

const propertyDetailsApi = createCloudRunAxios("aiPropertyDetails");

// Reject non-JSON responses so HTML/text error pages don't silently
// resolve as `response.data`.
propertyDetailsApi.interceptors.response.use((response) => {
  const contentType = response.headers["content-type"];
  if (!contentType || !contentType.includes("application/json")) {
    logger.error("Non-JSON response from ai-property-details", {
      status: response.status,
      contentType,
      url: response.config.url,
    });
    throw new Error("Invalid response format from server");
  }
  return response;
});

// MLS Detail Response type that handles both raw and transformed data
export interface MLSDetailResponse {
  data: {
    listing: {
      address: {
        city: string;
        countyOrParish: string;
        stateOrProvince: string;
        unparsedAddress: string | null;
        zipCode: string;
      };
      leadTypes: Record<string, any>;
      listPriceLow: string;
      media: Array<{
        mediaCategory: string;
        mediaKey: string;
        mediaType: string;
        mediaURL: string;
        order: number;
      }>;
      mlsBoardCode: string;
      mlsNumber: string;
      listingKey: string;
      listingOffice: {
        ListOfficeName?: string;
        ListOfficeEmail?: string;
        ListOfficePhone?: string;
        ListOfficeID?: string;
        ListOfficeKey?: string;
        name?: string;
        email?: string;
        phone?: string;
        id?: string;
        key?: string;
      };
      listAgent?: {
        listagentfullname?: string;
        listagentemail?: string;
        listagentprefferedphone?: string;
        listagentofficephone?: string;
        attributioncontact?: string;
      };
      buyerAgent?: {
        buyeragentfullname?: string;
        buyeragentmlsid?: string;
        buyeragentofficephone?: string;
        buyerofficename?: string;
      };
      primaryPhotoUrl: string;
      displayRules?: {
        mustRemovePhotos?: boolean; // When true, all photos except the primary need to be removed from display
        mustRemovePrimaryPhoto?: boolean; // When true, the primary photo must be removed from display
        showMapLink?: boolean; // When false, the property must appear in list results, but cannot be pinned on a map
      };
      // RESO fields
      internetAddressDisplayYN?: boolean; // When False, property must be displayed but cannot display street address
      internetAutomatedValuationDisplayYN?: boolean; // Controls if valuations or price estimates can be shown
      internetConsumerCommentYN?: boolean; // Controls if the product allows for comments
      property: {
        bathroomsTotal: number;
        bedroomsTotal: number;
        garageSpaces: number;
        hasBasement: boolean;
        hasPool: boolean;
        view?: string;
        latitude: string;
        livingArea: string;
        listprice?: string;
        listpricelow?: string;
        longitude: string;
        lotSizeSquareFeet: string;
        lotSizeAcres?: string;
        propertySubType: string;
        propertyType: string;
        stories: number | null;
        subdivisionName: string;
        yearBuilt: number;
        modificationTimestamp?: string;
        isNewConstruction?: boolean;
        isRental?: boolean;
      };
      publicRemarks: string | null;
      schools: {
        elementarySchool: string | null;
        highSchool: string | null;
        middleOrJuniorSchool: string | null;
      };
      standardStatus: string;
      mlsstatus?: string;
    };
    public?: {
      Directions?: string;
      PublicRemarks?: string;
    };
  };
  timestamp: string;
  property?: any; // Added to support new API response format
}

// Extract NWMLS-specific display rules from a property's customfields blob.
// Shared between the primary-photo selection and the displayRules block.
function extractDisplayRules(property: any) {
  const customFields = property?.customfields || {};
  return {
    mustRemovePhotos: customFields.NWM_IDXMustRemovePhotosYN === true,
    mustRemovePrimaryPhoto:
      customFields.NWM_IDXMustRemovePrimaryPhotoYN === true,
    showMapLink: !(customFields.NWM_ShowMapLink === false),
  };
}

export async function getPropertyDetails(
  listingKey: string,
  tenantId?: string,
  mlsOsn?: string
): Promise<MLSDetailResponse> {
  const queryParams = new URLSearchParams();
  if (tenantId) queryParams.append("tenant_id", tenantId);
  if (mlsOsn) queryParams.append("mls_osn", mlsOsn);

  const queryString = queryParams.toString();
  const endpoint = `/property/${listingKey}${
    queryString ? `?${queryString}` : ""
  }`;

  let response: MLSDetailResponse;
  try {
    const axiosResponse = await propertyDetailsApi.get<MLSDetailResponse>(
      endpoint
    );
    response = axiosResponse.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const errorMessage =
        error.response.data?.message ||
        error.response.data?.error ||
        `Request failed with status ${error.response.status}`;
      logger.error(`ai-property-details error (${endpoint})`, {
        status: error.response.status,
        error: errorMessage,
        details: error.response.data,
      });
      throw new Error(errorMessage);
    }
    logger.error(`Error fetching property details (${endpoint})`, { error });
    throw error;
  }

  logger.debug("Raw MLS Detail response", { response });

  // Transform the new API structure to match the expected format if needed
  if (response.property && !response.data) {
    return transformRawPropertyResponse(response.property, response.timestamp);
  }

  return response;
}

// Map the raw ai-property-details `property` payload (flat, lowercase RESO
// fields) into the nested MLSDetailResponse shape the UI consumes. Input stays
// `any` per the existing RESO-transform convention; tightening it is Phase 4.
function transformRawPropertyResponse(
  property: any,
  timestamp?: string
): MLSDetailResponse {
  const displayRules = extractDisplayRules(property);
  const primaryPhotoUrls = getImageUrlsFromMedia(property.media || [], {
    mustRemovePhotos: displayRules.mustRemovePhotos,
    mustRemovePrimaryPhoto: displayRules.mustRemovePrimaryPhoto,
    maxImages: 1,
    debugLog: false,
  });
  const primaryPhotoUrl = primaryPhotoUrls[0] ?? "";

  return {
    data: {
      listing: {
        address: {
          city: property.city || "",
          countyOrParish: property.countyorparish,
          stateOrProvince: property.stateorprovince || "",
          unparsedAddress: property.unparsedaddress || null,
          zipCode: property.postalcode || "",
        },
        leadTypes: {},
        listPriceLow: property.listprice?.toString() || "0",
        media:
          property.media
            ?.filter((item: any) => {
              // Filter out items with Permission: "Private"
              if (item.Permission === "Private") {
                return false;
              }
              return isImageUrl(item.MediaURL, item.MediaType);
            })
            ?.map((item: any, index: number) => ({
              mediaCategory: "Photo",
              mediaKey: `photo-${index}`,
              mediaType: item.MediaType?.toLowerCase() || "jpeg",
              mediaURL: item.MediaURL || "",
              order: item.Order || index,
            }))
            ?.sort(
              (a: { order: number }, b: { order: number }) => a.order - b.order
            ) || [], // Sort by order property
        mlsBoardCode: property.originatingsystemname || "",
        mlsNumber: property.listingid || "",
        listingKey: property.listingkey || "",
        listingOffice: {
          name: property.listofficename || "",
        },
        listAgent: {
          listagentfullname: property.listagentfullname || "",
          listagentemail: property.listagentemail || "",
          listagentprefferedphone: property.listagentpreferredphone || "",
          listagentofficephone: property.listagentofficephone || "",
          attributioncontact: property.attributioncontact || "",
        },
        buyerAgent: {
          buyeragentfullname: property.buyeragentfullname || "",
          buyeragentmlsid: property.buyeragentmlsid || "",
          buyeragentofficephone: property.buyeragentofficephone || "",
          buyerofficename: property.buyerofficename || "",
        },
        primaryPhotoUrl,
        displayRules,
        // RESO fields
        internetAddressDisplayYN: property.internetaddressdisplayyn !== false, // Default to true if not specified
        //internetAutomatedValuationDisplayYN: property.internetautomatedvaluationdisplayyn !== false, // Default to true if not specified
        //internetConsumerCommentYN: property.internetconsumercommentyn !== false, // Default to true if not specified
        property: {
          bathroomsTotal:
            property.bathroomstotalinteger || property.bathroomstotal || 0,
          bedroomsTotal: property.bedroomstotal || 0,
          garageSpaces: property.garagespaces || 0, // Using garagespaces from new structure if available
          hasBasement: property.basement || false,
          hasPool: property.poolprivateyn || false,
          view: property.view || null,
          latitude: "",
          livingArea: property.livingarea?.toString() || "0",
          listprice: property.listprice || "",
          listpricelow: property.listpricelow || "",
          longitude: "",
          lotSizeSquareFeet: "0",
          lotSizeAcres: property.lotsizeacres || null,
          propertySubType: property.propertysubtype || "Residential",
          propertyType: property.propertytype || "Residential",
          stories: null,
          subdivisionName: property.subdivisionname || "",
          yearBuilt: property.yearbuilt || 0,
          modificationTimestamp:
            property.modificationtimestamp ||
            timestamp ||
            new Date().toISOString(),
          isNewConstruction: property.newconstructionyn || false,
          isRental: property.rentalyn || false,
        },
        publicRemarks: property.publicremarks || null,
        schools: {
          elementarySchool: property.elementaryschool || null,
          highSchool: property.highschool || null,
          middleOrJuniorSchool: property.middleorjuniorschool || null,
        },
        standardStatus: property.standardstatus || "Active",
        mlsstatus: property.mlsstatus || "",
      },
    },
    timestamp: timestamp || new Date().toISOString(),
    property, // Keep the original property data
  };
}
